import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Jisa, Model, Recorder, RecorderEvents } from 'type/type';
import { useModal } from 'components/common/ModalLayout';
import { getJisaAll, getModelAll, getModelByName, getRecorderByIdx, getRecorderEventsByMac, maxLengthCheck, putRecorder } from 'api/api';
import Loading from 'components/common/Loading';
import { AgentLoginInfoContext } from 'pages/Layout';
import { eventLabels } from 'pages/utils/commonUtils';

function ProductEdit() {
  const { agentLoginInfo } = useContext(AgentLoginInfoContext);
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { recorderIdx } = useParams();
  const [loading, setLoading] = useState(true);
  const [isInIp, setIsInIp] = useState<boolean>(false);
  // useForm
  const { register, formState: { errors, isValid } } = useForm<any>({
    mode: 'all',
  });
  //2024-11-21
  const [recorder, setRecorder] = useState<Recorder>(Object);
  const [jisaList, setJisaList] = useState<Jisa[]>([]);
  const [jisaCodeList, setJisaCodeList] = useState<string[]>([]);
  const [modelOne, setModelOne] = useState<Model|null>(null);
  const [isEvents, setIsEvents] = useState<boolean>(false);
  const [recorderEvents, setRecorderEvents] = useState<RecorderEvents|undefined>(undefined);

  const getJisa = async () => {
    try {
      const response = await getJisaAll(0, 0); // 데이터 가져오는 함수
      if (response.code === 200) {
        // `jisa_code`를 string[]로 변환
        const jisaCodeArr = Array.isArray(agentLoginInfo.jisa_code)
          ? (agentLoginInfo.jisa_code as string[])
          : [];
        setJisaCodeList(jisaCodeArr);
        setJisaList(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // 녹화기 PUSH 이벤트 설정
  const handlePushChange = (event: any) => {
    let { name, value } = event.target;
    name = name.replaceAll("_push", "");
  
    setRecorderEvents((prevData) => {
      if (typeof prevData === "undefined") return;
      const { idx, user_id, recorder_mac, ...rest } = prevData; // idx, user_id, recorder_mac를 분리
  
      // name에 해당하는 값이 { push: number; monitor: number } 객체인지 확인
      const currentData = prevData[name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>];

      // currentData가 객체이고 monitor가 존재할 때만 monitor에 접근
      const currentMonitor = (currentData && typeof currentData === 'object' && 'monitor' in currentData)
        ? currentData.monitor
        : 0;
  
      return {
        idx, // 기존 idx 값 유지
        user_id, // 기존 user_id 값 유지
        recorder_mac, // 기존 recorder_mac 값 유지
        ...rest, // 나머지 필드는 업데이트
        [name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>]: {
          push: Number(value), // push 값만 업데이트
          monitor: currentMonitor, // monitor 값은 변경되지 않음
        },
      };
    });
  };

  // 녹화기 MONITOR 이벤트 설정
  const handleMonitorChange = (event: any) => {
    let { name, value } = event.target;
    name = name.replaceAll("_monitor", "");
    setRecorderEvents((prevData) => {
      if (typeof prevData === "undefined") return;
      const { idx, user_id, recorder_mac, ...rest } = prevData; // idx, user_id, recorder_mac를 분리
  
      // name에 해당하는 값이 { push: number; monitor: number } 객체인지 확인
      const currentData = prevData[name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>];

      // currentData가 객체이고 push가 존재할 때만 push에 접근
      const currentPush = (currentData && typeof currentData === 'object' && 'push' in currentData)
        ? currentData.push
        : 0;

      return {
        idx, // 기존 idx 값 유지
        user_id, // 기존 user_id 값 유지
        recorder_mac, // 기존 recorder_mac 값 유지
        ...rest, // 나머지 필드는 업데이트
        [name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>]: {
          push: currentPush, // push 값은 변경되지 않음
          monitor: Number(value), // monitor 값만 업데이트
        },
      };
    });
  };
  
  const eventsSetting = async () => {
    if (!recorder.model) return showModal('alert', '녹화기 Model이 존재하지 않습니다.');
    const modelRes = await getModelByName(recorder.model);
    if (modelRes.code !== 200) return;
    setModelOne(modelRes.data);
    setIsEvents(prev => !prev);
  }

  const [modelList, setModelList] = useState<Model[]>([]);
  
  const getModel = async () => {
    try {
      const response = await getModelAll(0, 0);
      if (response.code === 200) setModelList(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  // 계약 onChange 이벤트
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    // console.log(name, value);
    if (name === "mac") {
      setRecorder((prevData) => ({
        ...prevData,
        [name]: value.toUpperCase(),
      }));
    }
    else if (name.includes('in_ip')) {
      let val: string;
      const inIp1 = recorder?.in_ip?.split('.')[0] || "";
      const inIp2 = recorder?.in_ip?.split('.')[1] || "";
      const inIp3 = recorder?.in_ip?.split('.')[2] || "";
      const inIp4 = recorder?.in_ip?.split('.')[3] || "";
      if (name === "in_ip1") { val = value + '.' + inIp2 + '.' + inIp3 + '.' + inIp4 }
      else if (name === "in_ip2") { val = inIp1 + '.' + value + '.' + inIp3 + '.' + inIp4 }
      else if (name === "in_ip3") { val = inIp1 + '.' + inIp2 + '.' + value + '.' + inIp4 }
      else if (name === "in_ip4") { val = inIp1 + '.' + inIp2 + '.' + inIp3 + '.' + value }
      setRecorder((prevData) => ({
        ...prevData,
        'in_ip': val,
      }));
    }
    else {
      setRecorder((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
    if (name === "connect_type") {
      value === 'IP' ? setIsInIp(true) : setIsInIp(false);
    }
  }

  const _editRecorder = async () => {
    try {
      recorder.recorder_events = recorderEvents;
      const recorderRes = await putRecorder(recorder);
      if (recorderRes.code === 200) {
        showModal('alert','장비수정이 완료되었습니다.', {confirm: false, cancel: false},
          () => {
            navigate(-1);
          },
          () => {
            navigate(-1);
          },
        );
      } else if (recorderRes.code === 411) {
        showModal('alert', '사용중인 MAC입니다.');
      } else {
        showModal('alert', recorderRes.message);
      }
    } catch (error) {
      console.log(error);
    }
  }

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  const getRecorder = async () => {
    try {
      setLoading(true);
  
      // 병렬 실행으로 성능 개선
      const [modelRes, jisaRes, recorderRes] = await Promise.all([
        getModel(),
        getJisa(),
        getRecorderByIdx(Number(recorderIdx)),
      ]);
      if (recorderRes.code === 200) {
        setRecorder({
          ...recorderRes.data,
          manager_id: agentLoginInfo.id,
        });
        const recorderEventsRes = await getRecorderEventsByMac(recorderRes.data.mac);
        if (recorderRes.code === 200) {
          setRecorderEvents(recorderEventsRes.data);
        }

        setIsInIp(recorderRes.data.connect_type === "IP");
      } else {
        showModal("alert", recorderRes.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRecorder();
  },[]);
  
  useEffect(() => {
    const handleResize = () => {
      window.scrollTo(0, document.body.scrollHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (
    <>
      {loading ? <Loading /> :
        <div className={`${hubLayout.cnt_box}`}>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.sec_title}`}>
              <h2>고객 장비 관리</h2>
              <span onClick={() => navigate(-1)}>X</span>
            </div>
            {!isEvents ?
              <>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>장비 정보</h2>
                <div className={`${hubLayout.acc_item_info}`}>
                  <dl>
                    <dt>고객 ID</dt>
                    <dd>
                      <input type="text" className={`${errors.user_id && hubLayout.border_red}`} readOnly
                        placeholder="고객 ID" value={recorder.user_id} 
                        {...register(`user_id`, {
                          required: true,
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>계약번호</dt>
                    <dd>
                      <input type="text" className={`${errors.contract_no && hubLayout.border_red}`} readOnly
                        placeholder="계약번호" value={recorder.contract_no} 
                        {...register(`contract_no`, {
                          required: true,
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>MAC</dt>
                    <dd className={`${hubLayout.display_flex}`}>
                      <input type="text" className={`${errors.mac && hubLayout.border_red}`} placeholder="MAC" value={recorder.mac} readOnly
                        {...register(`mac`, {
                          required: {
                            value: true,
                            message: 'MAC을 입력하세요',
                          },
                          pattern : {
                            value: /^([0-9A-F]{2}){5}([0-9A-F]{2})$/g,
                            message: '정확한 MAC을 입력해주세요.',
                          },
                          onChange: handleChange
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>지사</dt>
                    <dd>
                      <div className={`${hubLayout.inp_select}`}>
                        <select className={`${hubLayout.inp_select}`} value={recorder.jisa_code} 
                          {...register(`jisa_code`, {
                            onChange: handleChange
                          })} >
                          {jisaList.map((item, idx) => (
                            <option key={`jisa_code_${idx}`} value={item.jisa_code} disabled={!jisaCodeList.includes(item.jisa_code)}>
                              {item.jisa_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(errors.jisa_code) && 
                        <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                          {errors.jisa_code?.message as string}
                        </p>
                      }
                    </dd>
                  </dl>
                  <dl>
                    <dt>Model</dt>
                    <dd>
                      <div className={`${hubLayout.inp_select}`}>
                        <select className={`${hubLayout.inp_select}`} value={recorder.model || ""}
                          {...register(`model`, {
                            onChange: handleChange
                          })} >
                          {modelList.map((model, idx) => (
                            <option key={`model_${idx}`} value={model.name} disabled={recorder.model !== model.name}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(errors.model) && 
                        <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                          {errors.model?.message as string}
                        </p>
                      }
                    </dd>
                  </dl>
                  <dl>
                    <dt>설치위치</dt>
                    <dd>
                      <input type="text" className={`${errors.reg_name && hubLayout.border_red}`}
                        placeholder="설치위치" value={recorder.reg_name}
                        {...register(`reg_name`, {
                          required: {
                            value: true,
                            message: '설치위치를 입력하세요',
                          },
                          onChange: handleChange
                        })}
                      />
                      {(errors.reg_name) && 
                        <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                          {errors.reg_name?.message as string}
                        </p>
                      }
                    </dd>
                  </dl>
                  <dl>
                    <dt>매장번호</dt>
                    <dd>
                      <input type="text" className={`${errors.store_key && hubLayout.border_red}`}
                        placeholder="매장번호" value={recorder.store_key}
                        {...register(`store_key`, {
                          required: {
                            value: false,
                            message: '매장번호를 입력하세요. 없을 경우 빈 값.',
                          },
                          onChange: handleChange
                        })}
                      />
                      {(errors.store_key) && 
                        <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                          {errors.store_key?.message as string}
                        </p>
                      }
                    </dd>
                  </dl>
                  <dl>
                    <dt>접속방식</dt>
                    <dd>
                      <span className={`${hubLayout.margin_right_10}`}>
                        <input type="radio" className={`${hubLayout.radio}`} value="DDNS" id='inp-ddns' checked={recorder.connect_type === 'DDNS'}
                          {...register(`connect_type`, {
                            required: true,
                            onChange: handleChange
                          })}
                        />
                        <label htmlFor='inp-ddns'>DDNS</label>
                      </span>
                      <span className={`${hubLayout.margin_right_10}`}>
                        <input type="radio" className={`${hubLayout.radio}`} value="IP" id='inp-ip' checked={recorder.connect_type === 'IP'}
                          {...register(`connect_type`, {
                            required: true,
                            onChange: handleChange
                          })}
                        />
                        <label htmlFor='inp-ip'>고정 IP</label>
                      </span>
                    </dd>
                  </dl>
                  {isInIp &&
                    <>
                      <dl>
                        <dt>내부 IP</dt>
                        <dd>
                          <input type="text" className={`${errors.in_ip1 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                            value={recorder.in_ip && recorder.in_ip.split('.')[0]}
                            {...register(`in_ip1`, {
                              required: {
                                value: true,
                                message: 'IP를 입력하세요.',
                              },
                              onChange: (e:any) => {
                                maxLengthCheck(e);
                                handleToNumericValue(e);
                              }
                            })}
                          />
                          .
                          <input type="text" className={`${errors.in_ip2 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                            value={recorder.in_ip && recorder.in_ip.split('.')[1]} 
                            {...register(`in_ip2`, {
                              required: {
                                value: true,
                                message: 'IP를 입력하세요.',
                              },
                              onChange: (e:any) => {
                                maxLengthCheck(e);
                                handleToNumericValue(e);
                              }
                            })}
                          />
                          .
                          <input type="text" className={`${errors.in_ip3 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                            value={recorder.in_ip && recorder.in_ip.split('.')[2]} 
                            {...register(`in_ip3`, {
                              required: {
                                value: true,
                                message: 'IP를 입력하세요.',
                              },
                              onChange: (e:any) => {
                                maxLengthCheck(e);
                                handleToNumericValue(e);
                              }
                            })}
                          />
                          .
                          <input type="text" className={`${errors.in_ip4 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                            value={recorder.in_ip && recorder.in_ip.split('.')[3]} 
                            {...register(`in_ip4`, {
                              required: {
                                value: true,
                                message: 'IP를 입력하세요.',
                              },
                              onChange: (e:any) => {
                                maxLengthCheck(e);
                                handleToNumericValue(e);
                              }
                            })}
                          />
                          {(errors.in_ip1 || errors.in_ip2 || errors.in_ip3 || errors.in_ip4) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors?.in_ip1?.message as string || errors?.in_ip2?.message as string || errors?.in_ip3?.message as string || errors?.in_ip4?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                      <dl>
                        <dt>내부 PORT</dt>
                        <dd>
                          <input type="text" className={`${errors.in_port && hubLayout.border_red}`}
                            placeholder="내부 PORT" value={recorder.in_port} 
                            {...register(`in_port`, {
                              required: {
                                value: true,
                                message: '내부 PORT를 입력하세요.',
                              },
                              onChange: handleToNumericValue
                            })}
                          />
                          {(errors.in_port) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors.in_port?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                    </>
                  }
                </div>
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => navigate(-1)}>취소</button>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid} onClick={eventsSetting}>다음</button>
                </div>
              </>
            :
              <>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>이벤트 설정</h2>
                <div className={`${hubLayout.acc_item_info}`}>
                  <dl>
                    <dt>고객 ID</dt>
                    <dd>
                      <input type="text" className={`${errors.user_id && hubLayout.border_red}`} readOnly
                        placeholder="고객 ID" value={recorder.user_id} 
                        {...register(`user_id`, {
                          required: {
                            value: true,
                            message: '고객 ID를 입력하세요',
                          },
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>계약번호</dt>
                    <dd>
                      <input type="text" className={`${errors.contract_no && hubLayout.border_red}`} readOnly maxLength={9} placeholder="계약번호" value={recorder.contract_no} />
                    </dd>
                  </dl>
                  <dl>
                    <dt>MAC</dt>
                    <dd className={`${hubLayout.display_flex}`}>
                      <input type="text" className={`${errors.mac && hubLayout.border_red}`} placeholder="MAC" value={recorder.mac} readOnly />
                    </dd>
                  </dl>
                  <div style={{ borderRadius: "0.5rem", border: '1px solid #1234', marginBottom: "1rem" }}>
                    <dl><dt><b>PUSH 설정</b></dt></dl>
                    {modelOne && (() => {
                      if (!recorderEvents) {
                        return <dl><dt>이벤트 없음</dt></dl>;
                      }
                      const filteredEvents = Object.entries(eventLabels).filter(([key]) => {
                        const eventValue = modelOne[key];
                        const event = recorderEvents[key];
                        return event && typeof event === 'object' && 'push' in event && eventValue === 1;
                      });

                      if (filteredEvents.length === 0) {
                        return <dl><dt>이벤트 없음</dt></dl>;
                      }

                      return filteredEvents.map(([key, label]) => {
                        const event = recorderEvents[key];
                        if (event && typeof event === 'object' && 'push' in event) {
                          return (
                            <dl key={`${key}_push`}>
                              <dt>{label}</dt>
                              <dd className={`${hubLayout.display_flex}`}>
                                {[0, 1, 2, 3].map((val) => (
                                  <span key={val} className={`${hubLayout.margin_right_10}`}>
                                    <input
                                      type="radio"
                                      className={`${hubLayout.radio}`}
                                      value={val}
                                      id={`inp-${key}_push-${val}`}
                                      checked={event.push === val}
                                      onChange={handlePushChange}
                                      name={`${key}_push`}
                                    />
                                    <label htmlFor={`inp-${key}_push-${val}`}>
                                      {["OFF", "경계", "해제", "상시"][val]}
                                    </label>
                                  </span>
                                ))}
                              </dd>
                            </dl>
                          );
                        }
                      });
                    })()}
                  </div>
                  {((recorder.contract?.service_code as any).control_service !== 0) &&
                    <div style={{ borderRadius: "0.5rem", border: '1px solid #1234' }}>
                      <dl><dt><b>관제 설정</b></dt></dl>
                      {modelOne && (() => {
                        if (!recorderEvents) {
                          return <dl><dt>이벤트 없음</dt></dl>;
                        }
                        const filteredEvents = Object.entries(eventLabels).filter(([key]) => {
                          const eventValue = modelOne[key];
                          const event = recorderEvents[key];
                          return event && typeof event === 'object' && 'monitor' in event && eventValue === 1;
                        });

                        if (filteredEvents.length === 0) {
                          return <dl><dt>이벤트 없음</dt></dl>;
                        }

                        return filteredEvents.map(([key, label]) => {
                          const event = recorderEvents[key];
                          if (event && typeof event === 'object') {
                            return (
                              <dl key={`${key}_monitor`}>
                                <dt>{label}</dt>
                                <dd className={`${hubLayout.display_flex}`}>
                                  {[0, 1, 2, 3].map((val) => (
                                    <span key={val} className={`${hubLayout.margin_right_10}`}>
                                      <input
                                        type="radio"
                                        className={`${hubLayout.radio}`}
                                        value={val}
                                        id={`inp-${key}_monitor-${val}`}
                                        checked={event.monitor === val}
                                        onChange={handleMonitorChange}
                                        name={`${key}_monitor`}
                                      />
                                      <label htmlFor={`inp-${key}_monitor-${val}`}>
                                        {["OFF", "경계", "해제", "상시"][val]}
                                      </label>
                                    </span>
                                  ))}
                                </dd>
                              </dl>
                            );
                          }
                        });
                      })()}
                    </div>
                  }
                </div>
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => setIsEvents(prev => !prev)}>이전</button>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid} onClick={_editRecorder}>수정</button>
                </div>
              </>
            }
          </div>
        </div>
      }
    </>
  )
}

export default ProductEdit;